// Share dialog for saved report definitions (reporting foundation R6).
// Owner-side, gated on reports:share by the caller. Visibility: Private /
// Specific roles / Whole company (tenant — owner only). Role shares can be
// BLOCKED server-side (400 code 'share_blocked') when the definition contains
// a column invisible to a recipient role — that message is surfaced
// prominently inside the dialog instead of a toast.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/features/shared/components/ui/button';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import { Label } from '@/features/shared/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/features/shared/components/ui/radio-group';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Loader2, Share2, ShieldAlert } from 'lucide-react';
import type { SavedReportVisibility } from '@/services/savedReportService';

/** Canonical shareable tenant roles (server vocabulary). */
export const SHARE_ROLE_OPTIONS: { key: string; label: string }[] = [
  { key: 'hr_manager', label: 'HR Manager' },
  { key: 'finance', label: 'Finance' },
  { key: 'hr_staff', label: 'HR Staff' },
  { key: 'manager', label: 'Manager' },
];

export const shareRoleLabel = (key: string): string =>
  SHARE_ROLE_OPTIONS.find((o) => o.key === key)?.label ?? key;

export interface ShareReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current persisted state — the dialog re-seeds from these on open. */
  visibility: SavedReportVisibility;
  sharedRoles: string[];
  /** Tenant-wide publishing stays owner-only. */
  canPublishTenant: boolean;
  /**
   * Persists the new sharing state (updateSavedReport). Reject with the axios
   * error to surface 'share_blocked' / 403 messages inside the dialog.
   */
  onSubmit: (visibility: SavedReportVisibility, sharedRoles: string[]) => Promise<void>;
}

type Choice = 'private' | 'role' | 'tenant';

export const ShareReportDialog: React.FC<ShareReportDialogProps> = ({
  open,
  onOpenChange,
  visibility,
  sharedRoles,
  canPublishTenant,
  onSubmit,
}) => {
  const { t } = useTranslation('reports');

  const [choice, setChoice] = useState<Choice>('private');
  const [roles, setRoles] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  // Re-seed from the persisted state every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setChoice(visibility === 'role' || visibility === 'tenant' ? visibility : 'private');
    setRoles(sharedRoles);
    setError('');
    setSaving(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const toggleRole = (key: string) =>
    setRoles((cur) => (cur.includes(key) ? cur.filter((r) => r !== key) : [...cur, key]));

  const submit = async () => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await onSubmit(choice, choice === 'role' ? roles : []);
      onOpenChange(false);
    } catch (e: any) {
      const data = e?.response?.data;
      if (data?.code === 'share_blocked') {
        // The recipient-can't-see-fields block — the server names fields + roles.
        setError(
          data?.message ||
            t(
              'share.blocked',
              'Sharing blocked: this report contains fields the selected roles are not allowed to see.',
            ),
        );
      } else if (e?.response?.status === 403) {
        setError(
          data?.message ||
            t('share.forbidden', 'You do not have permission to share reports this way.'),
        );
      } else {
        setError(
          data?.message ||
            data?.detail ||
            t('share.failed', 'Could not update sharing for this report.'),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const roleChoiceInvalid = choice === 'role' && roles.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('share.title', 'Share report')}</DialogTitle>
          <DialogDescription>
            {t(
              'share.body',
              'Control who can see and run this saved report. Recipients always see it under their own permissions.',
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <RadioGroup value={choice} onValueChange={(v) => setChoice(v as Choice)} className="space-y-2">
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2.5 cursor-pointer hover:border-slate-300 transition-colors">
              <RadioGroupItem value="private" id="share-private" className="mt-0.5" />
              <span>
                <span className="block text-sm font-medium text-foreground">
                  {t('share.private', 'Private')}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t('share.privateHint', 'Only you can see this report.')}
                </span>
              </span>
            </label>
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2.5 cursor-pointer hover:border-slate-300 transition-colors">
              <RadioGroupItem value="role" id="share-role" className="mt-0.5" />
              <span>
                <span className="block text-sm font-medium text-foreground">
                  {t('share.roles', 'Specific roles')}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {t('share.rolesHint', 'Everyone holding a selected role can see and run it.')}
                </span>
              </span>
            </label>
            {canPublishTenant && (
              <label className="flex items-start gap-3 rounded-lg border border-slate-200 px-3 py-2.5 cursor-pointer hover:border-slate-300 transition-colors">
                <RadioGroupItem value="tenant" id="share-tenant" className="mt-0.5" />
                <span>
                  <span className="block text-sm font-medium text-foreground">
                    {t('share.tenant', 'Whole company')}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {t('share.tenantHint', 'Everyone with reports access sees it.')}
                  </span>
                </span>
              </label>
            )}
          </RadioGroup>

          {choice === 'role' && (
            <div className="rounded-lg border border-slate-200 px-3 py-2.5 space-y-2">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                {t('share.pickRoles', 'Share with')}
              </p>
              {SHARE_ROLE_OPTIONS.map((o) => (
                <label key={o.key} className="flex items-center gap-2 text-sm cursor-pointer">
                  <Checkbox checked={roles.includes(o.key)} onCheckedChange={() => toggleRole(o.key)} />
                  <span>{t(`share.role.${o.key}`, o.label)}</span>
                </label>
              ))}
              {roleChoiceInvalid && (
                <p className="text-xs text-muted-foreground">
                  {t('share.pickAtLeastOne', 'Pick at least one role.')}
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 rounded-lg border border-danger bg-danger-soft px-3 py-2.5 text-sm text-danger-strong">
              <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={saving || roleChoiceInvalid}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Share2 className="w-4 h-4 mr-2" />
            )}
            {t('share.save', 'Update sharing')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareReportDialog;
